import {
  MyCohortResponseExperienceDistribution,
  ExperienceYearsRangeEnum,
} from '@platform/client/schemas';
import { ProgressBar } from '@platform/components';
import { cx } from 'classix';
import { Fragment } from 'react';

export const CohortHighlightsSection = ({
  totalMembers,
  countries,
  continents,
  languages,
  skills,
  experienceDistribution,
}: {
  totalMembers: number;
  countries: number;
  continents: number;
  languages: number;
  skills: number;
  experienceDistribution: MyCohortResponseExperienceDistribution;
}) => {
  return (
    <div className='w-full flex flex-col xl:flex-row gap-4'>
      <ExperienceYearsCard experienceLevels={experienceDistribution} totalMembers={totalMembers} />
      {/* Desktop */}
      <div className='hidden xl:flex gap-4 flex-1'>
        <CohortHighlightCards
          countries={countries}
          continents={continents}
          languages={languages}
          skills={skills}
        />
      </div>
      {/* Mobile */}
      <div className='xl:hidden bg-white shadow-sm border border-gray-100 rounded-xl px-4 py-5 grid grid-cols-3'>
        <CohortHighlightCards
          countries={countries}
          continents={continents}
          languages={languages}
          skills={skills}
        />
      </div>
    </div>
  );
};

const CohortHighlightCards = ({
  countries,
  continents,
  languages,
  skills,
}: {
  countries: number;
  continents: number;
  languages: number;
  skills: number;
}) => {
  return (
    <>
      <CohortHighlightCard
        title='Countries'
        value={countries}
        supportText={`Across ${continents} continents`}
      />
      <CohortHighlightCard
        title='Languages'
        value={languages}
        supportText='Spoken in this cohort'
      />
      <CohortHighlightCard title='Skills' value={skills} supportText='Across this cohort' isLast />
    </>
  );
};

const CohortHighlightCard = ({
  title,
  value,
  supportText,
  isLast = false,
}: {
  title: string;
  value: number;
  supportText: string;
  isLast?: boolean;
}) => {
  return (
    <div
      className={cx(
        'bg-white xl:border border-r xl:shadow-sm border-gray-100 xl:rounded-xl xl:p-5 flex flex-col gap-1 items-center xl:justify-center w-full ',
        isLast ? 'border-r-0' : '',
      )}
    >
      <h1 className='text-[32px] xl:text-display-lg text-brand-500 font-bold'>{value}</h1>
      <h2 className='text-sm xl:text-lg text-gray-700 font-semibold'>{title}</h2>
      <p className='text-xs xl:text-base text-gray-500 text-center lg:max-w-fit max-w-[4rem]'>
        {supportText}
      </p>
    </div>
  );
};

const EXPERIENCE_LEVEL_ORDER: ExperienceYearsRangeEnum[] = [
  '0-2 years',
  '3-5 years',
  '6-10 years',
  '11+ years',
];

const ExperienceYearsCard = ({
  experienceLevels,
  totalMembers,
}: {
  experienceLevels: Record<string, number>;
  totalMembers: number;
}) => {
  return (
    <div className='bg-white border border-gray-100 rounded-xl p-5 shadow-sm'>
      <h1 className='text-lg text-gray-700 font-bold'>Years of experience</h1>
      <p className='mb-5'>Range across the cohort</p>
      <div className='grid grid-cols-[76px_1fr_20px] xl:grid-cols-[76px_104px_20px] gap-2 items-center'>
        {EXPERIENCE_LEVEL_ORDER.map((range, index) => {
          const count = experienceLevels[range] || 0;
          return (
            <Fragment key={`exp-range-${index}`}>
              <p key={`range-${index}`} className='text-xs text-gray-500'>
                {range}
              </p>
              <ProgressBar
                key={`progress-${index}`}
                valueFormat='count'
                value={count}
                maxValue={totalMembers}
                valueLocation='none'
                barColor='brand-500'
                rounded
              />
              <p key={`count-${index}`} className='text-xs font-bold text-gray-500'>
                {count}
              </p>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};
